import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import toastr from "toastr";
import axios from "axios";
import jsPDF from 'jspdf';
import { GET_AGENT_SCORE_URL } from "../../../../../services/api/customer-support/quality-assurance/QualityAssuranceUrl";
import { GET_ONE_EMPLOYEE_URL } from "../../../../../services/api/shared/employees/EmployeesUrl";
import {
  getAgentScore,
  getEmployeesInCustomerSupport
} from "../../../../../actions/customer-support/quality-assurance/AgentsActions";

let autoTable;
try {
  autoTable = require('jspdf-autotable').default;
} catch (e) {
  autoTable = require('jspdf-autotable');
}

class SendReport extends Component {
  static propTypes = {
    getEmployeesInCustomerSupport: PropTypes.func.isRequired,
    employees: PropTypes.array.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      from: '',
      to: '',
      selectedEmployees: [],
      searchTerm: '',
      isGenerating: false
    };
    this.onChange = this.onChange.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.handleEmployeeSelection = this.handleEmployeeSelection.bind(this);
  }

  componentDidMount() {
    this.props.getEmployeesInCustomerSupport();
  }

  onChange = e => this.setState({
    [e.target.name]: e.target.value
  });

  onSearchChange = e => {
    this.setState({
      searchTerm: e.target.value
    });
  };

  handleEmployeeSelection = (e) => {
    const employeeId = e.target.value;
    const isChecked = e.target.checked;
    
    this.setState(prevState => {
      if (isChecked) {
        return {
          selectedEmployees: [...prevState.selectedEmployees, employeeId]
        };
      } else {
        return {
          selectedEmployees: prevState.selectedEmployees.filter(id => id !== employeeId)
        };
      }
    });
  };

  getFilteredEmployees = () => {
    const { employees } = this.props;
    const { searchTerm } = this.state;
    
    if (!searchTerm.trim()) {
      return employees || [];
    }
    
    return (employees || []).filter(employee => {
      const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
      const extension = employee.extension ? employee.extension.toString() : '';
      const search = searchTerm.toLowerCase();
      
      return fullName.includes(search) || extension.includes(search);
    });
  };

  clearSearch = () => {
    this.setState({
      searchTerm: ''
    });
  };

  calculateTotalScore = (row) => {
    const scoreFields = [
      'knowledge',
      'resolution', 
      'greetings',
      'communtication_skills',
      'problem_solving',
      'process_adherence',
      'closing',
      'customer_experience',
      'retention_pitch',
      'listening_skills',
      'retention_drive'
    ];
    
    return scoreFields.reduce((total, field) => {
      const value = parseFloat(row[field]) || 0;
      return total + value;
    }, 0);
  };

  calculateAveragePercentage = (scoreData) => {
    if (!scoreData || scoreData.length === 0) return 0;
    
    const totalScores = scoreData.map(row => this.calculateTotalScore(row));
    const sum = totalScores.reduce((acc, score) => acc + score, 0);
    const average = sum / totalScores.length;
    
    const maxPossibleScore = 100;
    const percentage = (average / maxPossibleScore) * 100;
    
    return Math.round(percentage * 100) / 100;
  };

  createTransposedPDFManually = (doc, employeeData, scoreData) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    doc.setFontSize(16);
    doc.text(`${employeeData.firstName} ${employeeData.lastName} - Quality Report`, 20, 15);
    
    const dates = scoreData.map(row => row.created_at ? row.created_at.substring(0, 10) : 'N/A');
    
    const scoreFields = [
      { key: 'extension', label: 'Extension' },
      { key: 'knowledge', label: 'Knowledge' },
      { key: 'resolution', label: 'Resolution' },
      { key: 'greetings', label: 'Greetings' },
      { key: 'communtication_skills', label: 'Communication Skills' },
      { key: 'problem_solving', label: 'Problem Solving' },
      { key: 'process_adherence', label: 'Process Adherence' },
      { key: 'closing', label: 'Closing' },
      { key: 'customer_experience', label: 'Customer Experience' },
      { key: 'retention_pitch', label: 'Retention Pitch' },
      { key: 'listening_skills', label: 'Listening Skills' },
      { key: 'retention_drive', label: 'Retention Drive' },
      { key: 'total', label: 'Total Score' },
      { key: 'feedback', label: 'Feedback' },
      { key: 'average', label: 'Average %' }
    ];
    
    const startX = 15;
    const startY = 25;
    const labelColWidth = 55;
    const availableWidth = pageWidth - startX - labelColWidth - 15;
    const dataColWidth = Math.min(availableWidth / dates.length, 60); 
    const rowHeight = 10;
    const feedbackRowHeight = 35; 
    
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    
    doc.text('Score Category', startX, startY);
    dates.forEach((date, index) => {
      const x = startX + labelColWidth + (index * dataColWidth);
      if (x + dataColWidth <= pageWidth - 15) {
        doc.text(date, x, startY);
      }
    });
    
    doc.line(startX, startY + 3, Math.min(pageWidth - 15, startX + labelColWidth + (dates.length * dataColWidth)), startY + 3);
    
    doc.setFont(undefined, 'normal');
    let currentY = startY + 12;
    
    scoreFields.forEach((field, rowIndex) => {
      const isLastRow = rowIndex === scoreFields.length - 1;
      let currentRowHeight = field.key === 'feedback' ? feedbackRowHeight : rowHeight;
      
      doc.setFont(undefined, 'bold');
      doc.text(field.label, startX, currentY);
      doc.setFont(undefined, 'normal');
      
      if (field.key === 'average') {
        const avgPercentage = this.calculateAveragePercentage(scoreData);
        doc.setFont(undefined, 'bold');
        doc.text(`${avgPercentage}%`, startX + labelColWidth + 5, currentY);
        doc.setFont(undefined, 'normal');
      } else {
        scoreData.forEach((row, dateIndex) => {
          const x = startX + labelColWidth + (dateIndex * dataColWidth);
          if (x + dataColWidth <= pageWidth - 15) {
            let value = '';
            
            if (field.key === 'total') {
              value = this.calculateTotalScore(row).toString();
              doc.setFont(undefined, 'bold');
            } else if (field.key === 'feedback') {
              value = row[field.key] || '';
              const feedbackWidth = dataColWidth - 5; 
              const lines = doc.splitTextToSize(value, feedbackWidth);
              const lineHeight = 3;
              const totalTextHeight = lines.length * lineHeight;
              const minHeight = feedbackRowHeight;
              currentRowHeight = Math.max(minHeight, totalTextHeight + 10); 
              
              lines.forEach((line, lineIndex) => {
                doc.text(line, x + 2, currentY + (lineIndex * lineHeight)); 
              });
              doc.setFont(undefined, 'normal');
              return;
            } else {
              value = row[field.key] ? row[field.key].toString() : '0';
            }
            
            if (field.key !== 'feedback') {
              const maxLength = Math.floor(dataColWidth / 1.5); 
              const displayValue = value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
              doc.text(displayValue, x, currentY);
            }
            
            if (field.key === 'total') {
              doc.setFont(undefined, 'normal');
            }
          }
        });
      }
      
      const lineY = currentY + (currentRowHeight - 6);
      doc.line(startX, lineY, Math.min(pageWidth - 15, startX + labelColWidth + (dates.length * dataColWidth)), lineY);
      
      currentY += currentRowHeight;
    });
    
    const tableHeight = currentY - startY + 5;
    doc.line(startX, startY - 5, startX, startY - 5 + tableHeight);
    doc.line(startX + labelColWidth, startY - 5, startX + labelColWidth, startY - 5 + tableHeight);
    
    dates.forEach((date, index) => {
      const x = startX + labelColWidth + ((index + 1) * dataColWidth);
      if (x <= pageWidth - 15) {
        doc.line(x, startY - 5, x, startY - 5 + tableHeight);
      }
    });
  };

  sendReport = async () => {
    const { from, to, selectedEmployees } = this.state;
    const { employees } = this.props;

    if (!from || !to || selectedEmployees.length === 0) {
      toastr.info("Please select at least one employee, from and to date of the report you want");
      return;
    }

    this.setState({ isGenerating: true });

    try {
      const employeeId = selectedEmployees[0];
      
      const employeeData = employees.find(emp => emp.id.toString() === employeeId.toString());
      
      if (!employeeData) {
        toastr.error(`Employee with ID ${employeeId} not found`);
        this.setState({ isGenerating: false });
        return;
      }

      const response = await axios.post(`${GET_AGENT_SCORE_URL}/${employeeId}`, { from, to }, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        toastr.warning(`No score data found for ${employeeData.firstName} ${employeeData.lastName} in the selected date range`);
        this.setState({ isGenerating: false });
        return;
      }

      const doc = new jsPDF('landscape');
      const scoreData = Array.isArray(response.data) ? response.data : [response.data];
      
      try {
        const dates = scoreData.map((row, index) => 
          row.created_at ? row.created_at.substring(0, 10) : `Record ${index + 1}`
        );
        
        const scoreFields = [
          { key: 'extension', label: 'Extension' },
          { key: 'knowledge', label: 'Knowledge' },
          { key: 'resolution', label: 'Resolution' },
          { key: 'greetings', label: 'Greetings' },
          { key: 'communtication_skills', label: 'Communication Skills' },
          { key: 'problem_solving', label: 'Problem Solving' },
          { key: 'process_adherence', label: 'Process Adherence' },
          { key: 'closing', label: 'Closing' },
          { key: 'customer_experience', label: 'Customer Experience' },
          { key: 'retention_pitch', label: 'Retention Pitch' },
          { key: 'listening_skills', label: 'Listening Skills' },
          { key: 'retention_drive', label: 'Retention Drive' },
          { key: 'total', label: 'Total Score' },
          { key: 'feedback', label: 'Feedback' },
          { key: 'average', label: 'Average %' }
        ];

        const headers = ['Score Category', ...dates];
        
        const tableBody = scoreFields.map(field => {
          const row = [field.label];
          
          if (field.key === 'average') {
            const avgPercentage = this.calculateAveragePercentage(scoreData);
            row.push(`${avgPercentage}%`);
            for (let i = 1; i < dates.length; i++) {
              row.push('');
            }
          } else {
            scoreData.forEach(dataRow => {
              if (field.key === 'total') {
                row.push(this.calculateTotalScore(dataRow).toString());
              } else if (field.key === 'feedback') {
                const feedback = dataRow[field.key] || '';
                row.push(feedback);
              } else {
                row.push(dataRow[field.key] ? dataRow[field.key].toString() : '0');
              }
            });
          }
          
          return row;
        });

        if (doc.autoTable) {
          doc.autoTable({
            head: [headers],
            body: tableBody,
            startY: 20,
            margin: { top: 10, left: 10, right: 10 },
            styles: { 
              fontSize: 7,
              cellPadding: 1.5,
              overflow: 'linebreak',
              cellWidth: 'auto' 
            },
            headStyles: {
              fillColor: [41, 128, 185],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 5
            },
            columnStyles: {
              0: { 
                cellWidth: 120,
                fontStyle: 'bold',
                fillColor: [236, 240, 241]
              }
            },
            didParseCell: function (data) {
              if (data.row.index === 12 && data.column.index > 0) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [255, 255, 204];
              }
              if (data.row.index === 14) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [204, 255, 204];
              }
              
              if (data.row.index === 13) {
                data.cell.styles.minCellHeight = 25; 
                data.cell.styles.valign = 'top';
                data.cell.styles.cellWidth = 'auto'; 
                data.cell.styles.overflow = 'linebreak'; 
              }
            },
            tableWidth: 'auto',
            theme: 'grid'
          });
          
          doc.setFontSize(16);
          doc.text(`${employeeData.firstName} ${employeeData.lastName} - Quality Report`, 20, 15);
        } else {
          toastr.Error('autoTable not available');
        }
        
      } catch (autoTableError) {
        this.createTransposedPDFManually(doc, employeeData, scoreData);
      }

      const fileName = `${employeeData.firstName}_${employeeData.lastName}_report.pdf`;
      doc.save(fileName);

      this.setState({
        selectedEmployees: [],
        isGenerating: false
      });

      toastr.success("Report generated successfully!");

    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        toastr.error(`API Error: ${error.response.data.message}`);
      } else {
        toastr.error(`Error: ${error.message}`);
      }
      this.setState({ isGenerating: false });
    }
  };

  render() {
    const { employees } = this.props;
    const { from, to, searchTerm, selectedEmployees, isGenerating } = this.state;

    const filteredEmployees = this.getFilteredEmployees();

    const employeesList = filteredEmployees.length > 0 ? filteredEmployees.map((employee) => {
      const isSelected = selectedEmployees.includes(employee.id.toString());
      
      return (
        <tr key={employee.id}>
          <td>
            <div className="d-flex">
              <input 
                type="checkbox"
                className="mt-1 mr-5"
                name="employee"
                value={employee.id.toString()}
                checked={isSelected}
                onChange={this.handleEmployeeSelection}
                disabled={isGenerating}
              />
              <h6>{employee.firstName} {employee.lastName}</h6>
            </div>
          </td>
          <td className="text-right">
            <h6>{employee.extension}</h6>
          </td>
        </tr>
      );
    }) : (
      <tr>
        <td colSpan="2" className="text-center">
          {searchTerm ? (
            <div>
              <small>No agents found matching "{searchTerm}"</small>
              <br />
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-1"
                onClick={this.clearSearch}
                disabled={isGenerating}
              >
                Clear search
              </button>
            </div>
          ) : (
            <small>No agents available</small>
          )}
        </td>
      </tr>
    );

    return (
      <div id="send-report" className="modal fade in" tabIndex="-1" role="dialog" aria-labelledby="myModalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title text-center" id="myModalLabel">Send Report</h4>
              <button type="button" className="close" data-dismiss="modal" aria-hidden="true" disabled={isGenerating}>×</button>
            </div>
            <div className="modal-body">
              <div className="col-12 d-flex mb-4 p-0">
                <div className="mr-5">
                  <label className="copy-font m-0 p-0">From</label>
                  <input 
                    type="date"
                    className="form-control"
                    name="from"
                    value={from}
                    onChange={this.onChange}
                    disabled={isGenerating}
                  />
                </div>
                <div className="ml-auto">
                  <label className="copy-font m-0 p-0">To</label>
                  <input 
                    type="date"
                    className="form-control"
                    name="to"
                    value={to}
                    onChange={this.onChange}
                    disabled={isGenerating}
                  />
                </div>
              </div>

              <div className="col-12 mb-3 p-0">
                <label className="copy-font m-0 p-0">Search Agents</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or extension..."
                    value={searchTerm}
                    onChange={this.onSearchChange}
                    disabled={isGenerating}
                  />
                  {searchTerm && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={this.clearSearch}
                        title="Clear search"
                        disabled={isGenerating}
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  )}
                </div>
                {searchTerm && (
                  <small className="text-muted">
                    Showing {filteredEmployees.length} of {employees ? employees.length : 0} agents
                  </small>
                )}
                {selectedEmployees.length > 0 && (
                  <small className="text-info d-block mt-1">
                    {selectedEmployees.length} agent{selectedEmployees.length > 1 ? 's' : ''} selected
                  </small>
                )}
              </div>

              <table className="table score-agent-table copy-font">
                <thead>
                  <tr>
                    <th>Select Agent</th>
                    <th className="text-right">Extension</th>
                  </tr>
                </thead>
                <tbody>
                  {employeesList}
                  <tr>
                    <td colSpan="2" className="text-right">
                      <button 
                        className="btn btn-green"
                        type="submit"
                        onClick={this.sendReport}
                        disabled={selectedEmployees.length === 0 || !from || !to || isGenerating}
                      >
                        {isGenerating ? 'Generating Report...' : 'Send Report'}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  employees: state.employeesByDepartment.employeesByDepartment,
});

export default connect(mapStateToProps, {
  getEmployeesInCustomerSupport, 
  getAgentScore
})(SendReport);